// MAIN-881: pure rules for the admin Edit Order item list.
//
// The page used to decide whether a row showed the menu picker with
// `!field._id && !menu`, where `menu` was the result of ONE lazy menu query
// shared by the whole page. Once any item had been picked that result stayed
// set for the rest of the session, so every later "Add New Item" row skipped
// the picker and rendered as a saved item: "Item #7 — () x1 @ $0.00 = $0",
// with no way to choose anything. Saving then sent that blank row to the API,
// which stored it as a real $0 cart line with no menu item, so it came back
// on every later edit.
//
// A row's state now depends only on the row itself.

const hasText = (value) => typeof value === "string" && value.trim() !== "";

/**
 * True once a row is tied to a real menu item. The item dialog sets both the
 * menu id and the item name when an item is chosen, so a row with neither has
 * never been filled in.
 */
export function isCartItemIdentified(item) {
  return Boolean(item) && hasText(item?.menuId) && hasText(item?.itemName);
}

/**
 * The empty placeholder the old bug saved: no menu item, no name, no price.
 *
 * Only this shape counts as broken. Older saved lines can legitimately lack a
 * menu id — 637 rows migrated in May 2023 carry a price but no menu id or
 * name — and those are order history to keep exactly as they are.
 */
export function isBlankPlaceholder(item) {
  return (
    Boolean(item) &&
    !hasText(item?.menuId) &&
    !hasText(item?.itemName) &&
    !(Number(item?.cartTotalPrice) > 0)
  );
}

/**
 * How a row renders:
 * - removed:      marked for removal on save
 * - picker:       a new row that still needs a menu item chosen
 * - summary:      a picked item, or any saved line that is real order history
 * - unidentified: a SAVED blank placeholder left by the old bug, which must
 *                 be removed — never shown as a $0 item
 */
export function getCartItemMode(item) {
  if (item?.markedForDelete) return "removed";
  if (!item?._id) return isCartItemIdentified(item) ? "summary" : "picker";
  return isBlankPlaceholder(item) ? "unidentified" : "summary";
}

/** 1-based row numbers that cannot be saved as they are. */
export function findUnidentifiedRows(cart = []) {
  return cart
    .map((item, index) => ({ item, row: index + 1 }))
    .filter(({ item }) => {
      const mode = getCartItemMode(item);
      return mode === "picker" || mode === "unidentified";
    })
    .map(({ row }) => row);
}

/** The message that stops a save while any row has no menu item. */
export function describeUnidentifiedRows(rows) {
  if (!rows.length) return null;
  const list = rows.map((row) => `#${row}`).join(", ");
  return rows.length === 1
    ? `Item ${list} has no menu item selected. Choose an item from the menu, or remove the row, before saving.`
    : `Items ${list} have no menu item selected. Choose an item from the menu, or remove those rows, before saving.`;
}

/**
 * The cart sent with an edit: rows marked for removal are left out (the API
 * deletes any saved row it no longer receives), local ids are stripped, and
 * the saved row's id travels as `cartId` so the API updates it in place.
 */
export function buildCartSubmission(cart = [], orderId) {
  return cart
    .filter((item) => !item?.markedForDelete)
    .map((item) => {
      // eslint-disable-next-line no-unused-vars
      const { _id, cartId, addOns, markedForDelete, ...rest } = item;
      return {
        ...rest,
        uuid: item.uuid,
        orderId,
        cartId: _id || null,
        addOns:
          addOns?.map(({ _id: addOnId, subAddOns, ...addOn }) => ({
            ...addOn,
            subAddOns: subAddOns?.map(({ _id: subId, ...sub }) => sub) || [],
          })) || [],
      };
    });
}

/** A blank row for "Add New Item". */
export function newCartRow() {
  return {
    uuid: "",
    orderId: "",
    menuId: "",
    customerId: "",
    catererId: "",
    categoryId: "",
    isEditedOrder: true,
    cartType: "",
    itemName: "",
    cartInstruction: "",
    cartTotalPrice: 0,
    pricePerUnit: 0,
    quantity: 1,
    minimumQuantity: 0,
    totalPricePerUnit: 0,
    addOns: [],
  };
}
